import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import type { ICurrency } from '@model/currency';

import { apiClient } from '@services/apiClient';
import type { IAiCostSavingSettings } from '@model/aiCostSavingSettings';

interface IResponse {
  action_item_cost_saving_settings: IAiCostSavingSettings;
  cost_saving_template_currency: ICurrency | null;
  photos: string[];
  cost_saving_tooltips: { [key: string]: string };
  action_list_charts: Array<string>;
}

export const actionItemStaticDataKey = (actionItemId: string | number) => [
  'actionItemStaticData',
  +actionItemId,
];

export const actionItemStaticDataFn = (
  queryKey: ReturnType<typeof actionItemStaticDataKey>,
  signal: AbortSignal | undefined,
) =>
  apiClient.get<{ data: IResponse }>(`${APP_API_URL_V2}fsd/action-item/${queryKey[1]}/static-data`, {
    signal,
  });

const actionItemStaticDataSelect = ({ data }: AxiosResponse<{ data: IResponse }>) => {
  const photos = Object.values(data?.data?.photos || {}).map((photo: string) => {
    return { url: photo };
  });

  const chartsImages = data?.data?.action_list_charts.map((photo: string) => {
    return { src: photo };
  });

  return {
    costSavingSettings: data?.data?.action_item_cost_saving_settings,
    costSavingTooltips: data?.data?.cost_saving_tooltips,
    costSavingTemplateCurrency: data?.data?.cost_saving_template_currency,
    actionListChartsImages: chartsImages,
    photos,
  };
};

export const useActionItemStaticDataQuery = ({
  actionItemId,
  installationPointId,
}: {
  actionItemId?: string | number;
  installationPointId?: string | number;
}) => {
  return useQuery({
    // @ts-ignore TODO: update react-query to v5
    queryKey: actionItemStaticDataKey(actionItemId),
    queryFn: ({ queryKey, signal }: QueryFunctionContext<ReturnType<typeof actionItemStaticDataKey>>) =>
      actionItemStaticDataFn(queryKey, signal),
    enabled: !!actionItemId && !installationPointId && actionItemId !== 'onboarding',
    useErrorBoundary: true,
    cacheTime: 1000,
    select: actionItemStaticDataSelect,
    placeholderData: {
      data: {
        costSavingSettings: {},
        costSavingTooltips: {},
        costSavingTemplateCurrency: null,
        photos: [],
        actionListChartsImages: [],
      },
    },
  });
};
